import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { isEmpty, pathOr } from 'ramda';
import {
  AppSummarySectionConfig,
  EstimateConfirmationLabels,
  Labels,
  SdfCardLabels,
  SummaryLabels,
  ItemSummarySection
} from '../app.models';
import { getCountryConfig } from './../config/country-config';
import { LocationFinderService } from '../services/location-finder.service';
import { TransactionState } from '../store/transaction/types';
import { isSdfCardFlow } from '../config/app-config';

export type AppSummaryConfig = {
  receiverCountry: string;
  receiverState?: string;
  receiveAmount: number;
  receiveName: string;
  receiveMethod: string;

  sendAmount: number;
  paymentMethod: string;
  // looks like we'll soon need to split this for send/receive
  currency: string;
  fees: number;
  discounts?: number;
};

@Component({
    selector: 'app-summary',
    templateUrl: './summary.component.html',
    styleUrls: ['./summary.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class SummaryComponent implements OnInit {
  isSdfCardFlow: boolean;
  private _sections: AppSummarySectionConfig[] = [];
  private _summaryData!: TransactionState;
  private summaryLabels!: SummaryLabels;
  estimateConfirmLabels!: EstimateConfirmationLabels;
  locationLabels!: any;
  public currency: any;
  sdfCardLabels: SdfCardLabels;
  @Input() stateOptions: any[];

  @Input() public set sections(
    sections: AppSummarySectionConfig[] | undefined
  ) {
    this._sections = sections || [];
  }
  public get sections() {
    return this._sections;
  }

  @Input() locale!: string;

  @Input() public set summaryData(summaryData) {
    this._summaryData = summaryData;
    this.currency = summaryData?.destinationCountryData?.currency;
  }
  public get summaryData() {
    return this._summaryData;
  }

  @Input() isWalletFeeEnabled: boolean = false;

  @Input() flow: string;

  @Input() wallet!: string;

  private _exchangeRate!: string;
  @Input() public set exchangeRate(exchangeRate: string | number) {
    if (typeof exchangeRate === 'number') {
      this._exchangeRate = exchangeRate.toFixed(4);
    } else {
      this._exchangeRate = exchangeRate && parseFloat(exchangeRate).toFixed(4);
    }
  }
  public get exchangeRate() {
    return this._exchangeRate;
  }

  private _selectedLocation!: any;
  @Input() public set selectedLocation(selectedLocation: any) {
    this._selectedLocation = selectedLocation;
  }
  public get selectedLocation() {
    return this._selectedLocation;
  }

  private _config?: AppSummaryConfig;
  @Input() public set config(config: AppSummaryConfig) {
    this._config = config;
  }
  public get config() {
    return this._config!;
  }

  public _labels: any;
  @Input() public set labels(labels: Labels) {
    this._labels = labels;
    this.estimateConfirmLabels = labels.stellar.estimateConfirmation;
    this.summaryLabels = labels.stellar.summary;
    this.locationLabels = labels.stellar.location;
    this.sdfCardLabels = labels.stellar.sdfcard;
    if (this.summaryData && this._labels) {
      this._populateCardSections();
    }
  }
  public get labels() {
    return this._labels;
  }

  @Input() hideUserInfo: boolean = false
  @Input() paymentMethod: string = 'cash'

  @Output() public readonly next: EventEmitter<AppSummaryConfig> =
    new EventEmitter<AppSummaryConfig>();
  @Output() public readonly cancel: EventEmitter<void> =
    new EventEmitter<void>();

  discounts = '0.00'; // todo: get it from  api objectout

  constructor(public locationFinderService: LocationFinderService) {
    this.isSdfCardFlow = isSdfCardFlow;
  }

  ngOnInit() {
    if (!this.isSdfCardFlow) {
      this._populateSections(this.summaryData);
    } else {
      this._populateCardSections();
    }
  }

  getCountryLabel() {
    // return this.summaryLabels.sendCountry
    return this.summaryLabels.country;
  }

  getStateLabel() {
    // return this.summaryLabels.sendStateProvince
    return this.summaryLabels.stateProvince;
  }

  getStateName() {
    const country = getCountryConfig(
      this.summaryData?.sendValidateRequest?.destinationCountryCode
    );

    if (country && country.states) {
      const state = country.states.find((state) => {
        return (
          this.summaryData?.sendValidateRequest
            ?.destinationCountrySubdivisionCode! === state.code
        );
      });
      return state?.name;
    }
    return;
  }

  getTotalAmountLabel() {
    if(this.flow === 'deposit'){
      return this.sdfCardLabels?.common?.totalPayAmount;
    }
    else if(this.flow === "withdraw" && this.paymentMethod === 'bank'){
      return this.labels.stellar.estimateConfirmation.depositAmount;
    }
    else if(this.flow === "withdraw" && this.paymentMethod !== 'bank'){
      return this.sdfCardLabels?.common?.totalPickupAmount;
    }
    return '';
  }

  public _populateCardSections(): void {
    if (this.flow === 'deposit' && this.paymentMethod === 'cash') {
      console.log('in _populateCardSections, checkForSelectedLocation');
      this.checkForSelectedLocation();
    }
    const partnerWalletFee = this.sdfCardLabels?.common?.partnerWalletFee;
    const walletLabel = partnerWalletFee ? partnerWalletFee : 'Wallet Fees';

    const partnerFees: ItemSummarySection = {
      name: this.isWalletFeeEnabled ? walletLabel.replace("%s1", this.wallet.charAt(0).toUpperCase() + this.wallet.slice(1).toLowerCase()) : this.sdfCardLabels.common.fees,
      value: this.getPartnerFees(),
      sendCurrency: this.flow === 'deposit' ? this.summaryData?.sendValidateResponse?.sendAmountDetails?.totalAmount?.currencyCode! : this.summaryData?.sendValidateResponse?.payoutAmountDetails?.totalAmount?.currencyCode!,
      isBold: false,
      displayMinus: this.flow === 'withdraw' && Number(this.getPartnerFees()) > 0,
    }
    const fees = {
      name: this.summaryLabels.fees,
      value: this.getFees(),
      sendCurrency: this.flow === 'withdraw' ? this.summaryData?.sendValidateResponse?.payoutAmountDetails?.totalAmount?.currencyCode : this.summaryData?.sendValidateResponse?.sendAmountDetails?.fees?.currencyCode!,
      isBold: false,
      displayMinus: this.flow === 'withdraw' && Number(this.getFees()) > 0,
    }
    this._sections = [
      {
        title: {
          name: this.sdfCardLabels.reviewPage.paymentInformation,
          value: this.displayFxRate(),
          isBold: true,
        },
        items: [
          {
            name:
              this.flow === 'deposit'
                ? this.sdfCardLabels.common.yourPayAmount
                : this.labels.stellar.summary.withdrawalAmount,
            value: this.getAmount(),
            sendCurrency:
              this.flow === 'withdraw'
                ? this.summaryData?.sendValidateResponse?.payoutAmountDetails
                    ?.totalAmount?.currencyCode
                : this.summaryData?.sendValidateResponse?.sendAmountDetails
                    ?.amount?.currencyCode!,
            isBold: false,
          },
          {
            name: this.sdfCardLabels.landingPage.payment.payMethod,
            value: this.getPaymentMethod(),
            isBold: false,
          },
          {
            name: this.sdfCardLabels.landingPage.payment.receiveMethod,
            value: this.getReceiveMethod(),
            isBold: false,
          },       
          ...[partnerFees]
          ,{
            name: this.summaryLabels.taxes,
            value: this.getTaxes(),
            sendCurrency:
              this.flow === 'withdraw'
                ? this.summaryData?.sendValidateResponse?.payoutAmountDetails
                    ?.totalAmount?.currencyCode
                : this.summaryData?.sendValidateResponse?.sendAmountDetails
                    ?.taxes?.currencyCode!,
            isBold: false,
            displayMinus:
              this.flow === 'withdraw' && Number(this.getTaxes()) > 0,
          },
        ],
        footer: {
          name: this.getTotalAmountLabel(),
          value: this.getTotalUSDC(),
          sendCurrency:
            this.flow === 'deposit'
              ? this.getExchangeAmountCurrency()
              : this.summaryData?.sendValidateResponse?.payoutAmountDetails
                  ?.totalAmount?.currencyCode,
          isBold: true,
        },
      },
      {
        title: {
          name: this.sdfCardLabels.reviewPage.paymentLocation,
          isBold: true,
          isDepositOnlyItem: true,
          isCashOnlyItem: true,
        },
        items: [
          {
            name: this.selectedLocation?.locationDetails?.name,
            value: this.getAddressInfo(true),
            isBold: true,
          },
        ],
      },
      {
        title: {
          name: this.sdfCardLabels.common.myId,
          isBold: true,
          isCashOnlyItem: true,
        },
        items: [
          {
            name: this.sdfCardLabels.common.idType,
            value: this.getSelectedIdInfo(
              `${this.summaryData.sendValidateRequest?.sender?.primaryIdentification?.typeCode}`
            ),
          },
          {
            name: this.sdfCardLabels.common.idNumber,
            value: `${this.summaryData.sendValidateRequest?.sender?.primaryIdentification?.id}`,
          },
          {
            name: this.sdfCardLabels.common.countryOfIssue,
            value: getCountryConfig(
              this.summaryData.sendValidateRequest?.sender
                ?.primaryIdentification?.issueCountryCode
            ).name,
          },
          {
            name: this.sdfCardLabels.common.stateLabel,
            value: this.getSelectedStateInfo(
              `${this.summaryData.sendValidateRequest?.sender?.primaryIdentification?.issueCountrySubdivisionCode}`
            ),
          },
        ],
      },
      {
        title: {
          name: this.sdfCardLabels.common.myInfo,
          isBold: true,
          isCashOnlyItem: true,
        },
        items: [
          {
            name: this.sdfCardLabels.common.legalName,
            value: `${this.summaryData.sendValidateRequest?.sender?.firstName} ${this.summaryData.sendValidateRequest?.sender?.middleName} ${this.summaryData.sendValidateRequest?.sender?.lastName} ${this.summaryData.sendValidateRequest?.sender?.secondLastName}`,
          },
          {
            name: this.sdfCardLabels.common.dateOfBirth,
            value: this.getdateOfBirth(),
          },
          {
            name: this.sdfCardLabels.common.address,
            value: this.getAddressInfo(false),
          },
          {
            name: this.getCountryLabel(),
            value: getCountryConfig(
              this.summaryData?.sendValidateRequest?.sender?.address
                ?.countryCode
            ).name,
          },
          {
            name: this.sdfCardLabels.common.phoneNumber,
            value: `+${this.summaryData.sendValidateRequest?.sender?.mobilePhone?.countryDialCode} ${this.summaryData.sendValidateRequest?.sender?.mobilePhone?.number}`,
          },
        ],
      },
    ];

    if (this.flow === 'withdraw') {
      this._sections = this._sections.filter(
        (section: any) => !section?.title.isDepositOnlyItem
      );
    }

    if (this.hideUserInfo) {
      this._sections = this._sections.filter(
        (section: any) => !section?.title.isCashOnlyItem
      );
    }

    // if(!this.isEmpty(this.getStateName())) {
    //   const stateSection =  {
    //     name: this.getStateLabel(),
    //     value: this.getStateName()!,
    //     isBold: false,
    //   }
    //   this._sections.filter(section => {
    //     if(section.title === this.summaryLabels.receiver || section.title === "Personal Information"){
    //       return section.items.push(stateSection)
    //     }
    //     return section
    //   })
    // }
  }

  public _populateSections(summaryData: TransactionState): void {
    // const country = summaryData?.destinationCountryData?.country &&
    //   getCountryConfig(summaryData?.destinationCountryData?.country) || undefined

    // const state = summaryData?.destinationCountryData!.state &&
    //   country &&
    //   getStateConfig(country, summaryData?.destinationCountryData!.state) || undefined
    const exchangeAmount = {
      name: this.getReceiveAmountLabel(),
      value: this.getAmount(),
      sendCurrency: this.getExchangeAmountCurrency(),
      isBold: false,
    };

    const fees = {
      name: this.summaryLabels.fees,
      value: this.getFees(),
      sendCurrency:
        this.summaryData?.sendValidateResponse?.sendAmountDetails?.fees
          ?.currencyCode!,
      isBold: false,
      displayMinus: this.flow === 'withdraw' && Number(this.getFees()) > 0,
    };

    const taxes = {
      name: this.summaryLabels.taxes,
      value: this.getTaxes(),
      sendCurrency:
        this.summaryData?.sendValidateResponse?.sendAmountDetails?.taxes
          ?.currencyCode!,
      isBold: false,
      displayMinus: this.flow === 'withdraw' && Number(this.getTaxes()) > 0,
    };

    // const partnerFees: ItemSummarySection = { // TODO: not targeted for June 18th, also requirements changed for PartnerFees, so commenting out for now
    //   name: 'Partner Fees', // Todo: hardcoded label. Will be updated when language is finalized
    //   value: this.getPartnerFees(),
    //   sendCurrency: this.summaryData?.sendValidateResponse?.sendAmountDetails?.partnerFees?.currencyCode!,
    //   isBold: false,
    //   displayMinus: this.flow === 'withdraw' && Number(this.getPartnerFees()) > 0,
    // }

    // const partnerTaxes: itemSummarySection = { // TODO: not targeted for June 18th, also requirements changed for PartnerFees, so commenting out for now
    //   name: 'Partner Taxes', // Todo: hardcoded label. Will be updated when language is finalized
    //   value: this.getPartnerTaxes(),
    //   sendCurrency: this.summaryData?.sendValidateResponse?.sendAmountDetails?.partnerTaxes?.currencyCode!,
    //   isBold: false,
    //   displayMinus: this.flow === 'withdraw' && Number(this.getPartnerTaxes()) > 0,
    // }

    const costSummary = [];
    if (this.flow === 'withdraw') {
      costSummary.push(fees);
      costSummary.push(taxes);
      costSummary.push(exchangeAmount);
    } else {
      costSummary.push(exchangeAmount);
      costSummary.push(fees);
      costSummary.push(taxes);
      // if (!isNaN(partnerFees.value) && partnerFees.value !== null && typeof partnerFees.value !== "undefined") { costSummary.push(partnerFees); } // TODO: not targeted for June 18th, also requirements changed for PartnerFees, so commenting out for now
      // if (!isNaN(partnerTaxes.value) && partnerTaxes.value !== null && typeof partnerTaxes.value !== "undefined") { costSummary.push(partnerTaxes); } // TODO: not targeted for June 18th, also requirements changed for PartnerFees, so commenting out for now
    }

    this._sections = [
      {
        // title: this.summaryLabels.personalInformation,
        title: {
          name: this.summaryLabels.personalInformation,
          isBold: true,
        },
        items: [
          {
            name: this.summaryLabels.name,
            value: `${this.summaryData.sendValidateRequest?.sender?.firstName} ${this.summaryData.sendValidateRequest?.sender?.middleName} ${this.summaryData.sendValidateRequest?.sender?.lastName} ${this.summaryData.sendValidateRequest?.sender?.secondLastName}`,
            isBold: false,
          },
          {
            name: this.getCountryLabel(),
            value: getCountryConfig(
              this.summaryData?.sendValidateRequest?.destinationCountryCode
            ).name,
            isBold: false,
          },
        ],
      },
      {
        // title: this.summaryLabels.paymentInformation,
        title: {
          name: this.summaryLabels.paymentInformation,
          isBold: true,
        },
        items: [
          {
            name: this.summaryLabels.paymentMethod,
            value: this.getPaymentMethod(),
            isBold: false,
          },
          {
            name: this.summaryLabels.receiveMethod,
            value: this.getReceiveMethod(),
            isBold: false,
          },
        ],
      },
      {
        title: {
          name: this.summaryLabels.costSummary,
          isBold: true,
        },
        items: costSummary,
      },
    ];

    if (!this.isEmpty(this.getStateName())) {
      const stateSection = {
        name: this.getStateLabel(),
        value: this.getStateName()!,
        isBold: false,
      };
      this._sections.filter((section) => {
        if (
          section &&
          section?.title &&
          (section.title.name === this.summaryLabels.receiver ||
            section.title.name === 'Personal Information')
        ) {
          return section.items.push(stateSection);
        }
        return section;
      });
    }
  }

  getSelectedStateInfo(stateCode: string) {
    return (
      (this.stateOptions &&
        this.stateOptions.find((state) => state.code === stateCode)?.name) ||
      stateCode
    );
  }

  displayFxRate() {
    return this.getExchangeAmountCurrency() === 'USD'
      ? `1 USDC = 1 USD`
      : `1 USDC = 1 USD = ${
          this.exchangeRate
        } ${this.getExchangeAmountCurrency()}`;
  }

  getSelectedIdInfo(id: string) {
    return pathOr(
      id,
      ['stellar', 'enumsObj', 'PERSONAL_ID1_TYPE', id],
      this.labels
    );
  }

  checkForSelectedLocation() {
    if (isEmpty(this.selectedLocation)) {
      const customFieldsLoc = JSON.parse(
        pathOr(
          '',
          ['feeLookupResponse', 'additionalInfos', '0', 'value'],
          this.summaryData
        )
      );
      this.selectedLocation = customFieldsLoc;
    }
  }

  getAddressInfo(isLocationDetails: boolean): any {
    let add1 = '';
    let add2 = '';
    if (isLocationDetails) {
      add1 = this.locationFinderService.getFormattedAddress({
        addressLine1: this.selectedLocation?.locationDetails?.addressLine1,
        addressLine2: this.selectedLocation?.locationDetails?.addressLine2,
        addressLine3: this.selectedLocation?.locationDetails?.addressLine3,
      });

      add2 = this.locationFinderService.getFormattedAddress({
        city: this.selectedLocation?.locationDetails?.city,
        state: this.selectedLocation?.locationDetails?.state,
        postalCode: this.selectedLocation?.locationDetails?.postalCode,
        zipCode: this.selectedLocation?.locationDetails?.zipCode,
      });
    } else {
      const userAdd = this.summaryData.sendValidateRequest?.sender?.address;
      add1 = this.locationFinderService.getFormattedAddress({
        addressLine1: userAdd?.line1,
        addressLine2: userAdd?.line2,
      });

      add2 = this.locationFinderService.getFormattedAddress({
        city: userAdd?.city,
        state: userAdd?.countrySubdivisionCode?.slice(-2),
        postalCode: userAdd?.postalCode,
      });
    }
    return `${add1}\n${add2}`;
  }

  getdateOfBirth(): string {
    const dobArray =
      this.summaryData.sendValidateRequest?.sender?.dateOfBirth?.split('-');
    if (dobArray) {
      return `${dobArray[1]}/${dobArray[2]}/${dobArray[0]?.slice(-2)}`;
    }
    return '';
  }

  getFormattedWalletName() {
    if (this.wallet.toLowerCase() === 'vibrant') {
      return 'Vibrant';
    } else if (this.wallet.toLowerCase() === 'cinko') {
      return 'CiNKO';
    }
    return this.wallet;
  }

  getPaymentMethod() {
    if (this.flow === 'withdraw') {
      return this.wallet
        ? `${this.getFormattedWalletName()} ${this.summaryLabels.wallet}`
        : `${this.summaryLabels.wallet}`;
    } else if (this.flow === 'deposit') {
      return this.paymentMethod === 'debit' 
        ? this.labels.stellar.signIn.paymentMethods.debitCard 
        : this.summaryLabels.cash;
    }
    return this.summaryLabels.cash;
  }

  getReceiveMethod() {
    if (this.flow === 'withdraw') {
      return this.paymentMethod === 'bank' 
        ? this.labels.stellar.signIn.paymentMethods.bankAccount 
        : this.summaryLabels.cashPickup;
    }
    return this.wallet
      ? `${this.getFormattedWalletName()} ${this.summaryLabels.wallet}`
      : `${this.summaryLabels.wallet}`;
  }

  getExchangeRate() {
    if (this.flow === 'withdraw') {
      return `1.00 USDC = ${this.exchangeRate} ${this.summaryData
        ?.sendValidateResponse?.payoutAmountDetails?.amount?.currencyCode!}`;
    }
    return '1.00 USD = 1.00 USDC';
  }

  getTotalCostLabel() {
    /*if (this.flow === 'withdraw') {
      return this.summaryLabels.withdrawalFromWallet
    }*/
    // return this.summaryLabels.toPayAtLocation
    return this.flow === 'withdraw'
      ? this.summaryLabels.cashOutAmount
      : this.summaryLabels.amountToPay;
  }

  getReceiveAmountLabel() {
    /*if (this.flow === 'withdraw') {
      return this.summaryLabels.cashOutAmount
    }*/
    // return this.summaryLabels.cashInAmount
    return this.summaryLabels.exchangeAmount;
  }

  getTotalCost() {
    let totalAmount: string | number;
    totalAmount =
      this.flow === 'withdraw'
        ? this.summaryData?.sendValidateResponse?.payoutAmountDetails
            ?.totalAmount?.value!
        : this.summaryData?.sendValidateResponse?.sendAmountDetails?.totalAmount
            ?.value!;
    if (typeof totalAmount === 'number') {
      totalAmount = totalAmount.toFixed(2);
    } else {
      totalAmount = parseFloat(totalAmount).toFixed(2);
    }
    return parseFloat(totalAmount).toFixed(2);
  }

  getTotalCostCurrency() {
    return this.flow === 'withdraw'
      ? this.summaryData?.sendValidateResponse?.payoutAmountDetails?.totalAmount
          ?.currencyCode!
      : this.summaryData?.sendValidateResponse?.sendAmountDetails?.totalAmount
          ?.currencyCode!;
  }

  getTotalUSDC() {
    let totalAmount: string | number;
    totalAmount =
      this.flow === 'withdraw'
        ? this.summaryData?.sendValidateResponse?.payoutAmountDetails
            ?.totalAmount?.value!
        : this.summaryData?.sendValidateResponse?.sendAmountDetails?.totalAmount
            ?.value!;
    if (typeof totalAmount === 'number') {
      totalAmount = totalAmount.toFixed(2);
    } else {
      totalAmount = parseFloat(totalAmount).toFixed(2);
    }
    return parseFloat(totalAmount).toFixed(2);
  }

  getExchangeAmountCurrency() {
    return this.flow === 'withdraw'
      ? this.summaryData?.sendValidateResponse?.payoutAmountDetails?.amount
          ?.currencyCode!
      : this.summaryData?.sendValidateResponse?.sendAmountDetails?.amount
          ?.currencyCode!;
  }

  getAmount() {
    let amount: string | number;
    amount =
      this.flow === 'withdraw'
        ? this.summaryData?.sendValidateResponse?.sendAmountDetails?.totalAmount
            ?.value!
        : this.summaryData?.sendValidateResponse?.sendAmountDetails?.amount
            ?.value!;
    const fxRate =
      this.summaryData?.sendValidateResponse?.payoutAmountDetails?.fxRate || 1;
    amount = Number(amount);
    if (this.flow === 'withdraw') {
      return Number(amount * fxRate).toFixed(2);
    }

    if (typeof amount === 'number') {
      amount = amount.toFixed(2);
    } else {
      amount = parseFloat(amount).toFixed(2);
    }

    return parseFloat(amount).toFixed(2);
  }

  getFees() {
    const value =
      this.summaryData?.sendValidateResponse?.sendAmountDetails?.fees?.value;
    const fxRate =
      this.summaryData?.sendValidateResponse?.payoutAmountDetails?.fxRate || 1;
    if (this.flow === 'withdraw') {
      return Number(value * fxRate).toFixed(2);
    }
    return this.summaryData?.sendValidateResponse?.sendAmountDetails?.fees?.value?.toFixed(
      2
    );
  }

  getTaxes() {
    const value =
      this.summaryData?.sendValidateResponse?.sendAmountDetails?.taxes?.value ||
      0;
    const fxRate =
      this.summaryData?.sendValidateResponse?.payoutAmountDetails?.fxRate || 1;
    if (this.flow === 'withdraw') {
      return Number(value * fxRate).toFixed(2);
    }
    return (
      this.summaryData?.sendValidateResponse?.sendAmountDetails?.taxes?.value?.toFixed(
        2
      ) || 0.0
    );
  }

   getPartnerFees() {
    const valFlow = (this.flow === 'deposit' ) ? 
                      this.summaryData?.sendValidateResponse?.payoutAmountDetails?.partnerFees?.value :
                      this.summaryData?.sendValidateResponse?.sendAmountDetails?.partnerFees?.value;
    const fxRateFlow = (this.flow === 'deposit' ) ? 
                      this.summaryData?.sendValidateResponse?.sendAmountDetails?.fxRate :
                      this.summaryData?.sendValidateResponse?.payoutAmountDetails?.fxRate;

    const value = Number(valFlow)
    const fxRate = Number(fxRateFlow)
    return (!isNaN(value) && !isNaN(fxRate))? (value * fxRate).toFixed(2) : "0.00";
  }

  getReceiveAmount() {
    return (
      this.summaryData?.sendValidateResponse?.payoutAmountDetails?.amount?.value.toFixed(
        2
      ) +
      this.summaryData?.sendValidateResponse?.payoutAmountDetails?.fees?.value.toFixed(
        2
      )
    );
  }

  isEmpty(obj: any): boolean {
    if (!obj || obj === null) {
      return true;
    }
    return Object.keys(obj).length === 0;
  }
}
